import { Configuration } from '@h2/types';
import { 
  LanguageSettingsData, 
  WorkflowData, 
  WorkerAssignmentData, 
  FinalConfirmationData 
} from '../../stores/useWorkPresetStore';

// JamakeJob 타입 임포트 (필요시)
interface JamakeJobWithOrder {
  id: string;
  name: string;
  registeredAt: string;
  sourceLanguage: string;
  targetLanguage: string;
  jobType: string;
  jobScope: string;
  requirements?: string;
  glossary?: string;
  videoDuration: string;
  orderId: string;
  orderTitle: string;
}

// 언어 이름을 코드로 변환하는 헬퍼 함수
const convertLanguageNameToCode = (langName: string): string => {
  const langToCodeMap: { [key: string]: string } = {
    '한국어': 'ko',
    '영어': 'en', 
    '일본어': 'ja',
    '중국어': 'zh',
    '스페인어': 'es',
    '힌디어': 'hi'
  };
  return langToCodeMap[langName] || langName;
};

// WorkSettings 전용 Configuration 팩토리
export const createWorkSettingsConfiguration = (selectedJobs: JamakeJobWithOrder[] = []): Configuration => {
  // 선택된 job들로부터 기본값 추출
  // 원본언어와 번역언어들을 분리하여 추출
  const rawSourceLanguage = selectedJobs.length > 0 ? selectedJobs[0]?.sourceLanguage || '' : '';
  const rawTargetLanguages = selectedJobs.length > 0 
    ? Array.from(new Set(selectedJobs.map(job => job.targetLanguage))).filter(Boolean)
    : [];

  // 언어 이름을 코드로 변환
  const sourceLanguage = rawSourceLanguage ? convertLanguageNameToCode(rawSourceLanguage) : '';
  const targetLanguages = rawTargetLanguages.map(convertLanguageNameToCode);

  console.log('🔄 언어 변환:', {
    원본: { rawSourceLanguage, rawTargetLanguages },
    변환후: { sourceLanguage, targetLanguages }
  });

  const jobSummary = selectedJobs.length > 0 
    ? `선택된 작업: ${selectedJobs.length}개 작업 (${selectedJobs.map(job => `${job.name} (${job.orderId})`).join(', ')})`
    : '';

  return {
  id: 'work-settings',
  title: '작업 설정',
  description: '새로운 작업을 위한 상세 설정을 진행합니다.',
  sections: [
    {
      id: 'premium-selection',
      title: '작업 유형 선택',
      description: '프리미엄 작업 여부를 선택합니다.',
      fields: [
        {
          id: 'isPremiumWork',
          type: 'radio',
          label: '작업 유형',
          value: 'standard',
          options: [
            {
              value: 'standard',
              label: '일반 작업',
              description: '표준 품질의 작업입니다'
            },
            {
              value: 'premium',
              label: '프리미엄 작업',
              description: '높은 품질이 요구되는 작업입니다'
            }
          ],
          validation: {
            rules: [
              {
                type: 'required',
                message: '작업 유형을 선택해주세요'
              }
            ]
          }
        },
        {
          id: 'workPriorities',
          type: 'priority-selector',
          label: '작업 우선순위',
          value: [],
          customProps: {
            isPremium: false // 기본값, 실제로는 isPremiumWork 값에 따라 동적으로 설정됨
          },
          validation: {
            rules: [
              {
                type: 'custom',
                message: '작업 우선순위가 설정되었습니다',
                validator: () => true // 항상 유효 (선택적 필드)
              }
            ]
          }
        }
      ]
    },
    {
      id: 'preset-selection',
      title: '프리셋 선택',
      description: '기존 프리셋을 선택하거나 직접 설정합니다.',
      fields: [
        {
          id: 'selectedPresetId',
          type: 'preset-selector',
          label: '작업 프리셋',
          value: 'manual',
          placeholder: '프리셋을 검색하여 선택하거나 직접 설정하세요',
          customProps: {
            expectedSourceLanguage: rawSourceLanguage, // 예상되는 원본언어 전달 (원본 이름)
            expectedTargetLanguages: rawTargetLanguages // 예상되는 번역언어들 전달 (원본 이름)
          },
          validation: {
            rules: [
              {
                type: 'custom',
                message: '프리셋 선택이 완료되었습니다',
                validator: () => true // 프리셋은 항상 유효하다고 설정 (manual 포함)
              }
            ]
          }
        }
      ]
    },
    {
      id: 'language-settings',
      title: '언어 및 기본 정보',
      description: '작업의 언어와 기본 정보를 설정합니다.',
      fields: [
        {
          id: 'languageSettingsComponent',
          type: 'language-settings',
          label: '언어 설정',
          value: {
            sourceLanguage: sourceLanguage,
            targetLanguages: targetLanguages,
            tags: [],
            memo: jobSummary
          } as LanguageSettingsData,
        customProps: {
          isLanguageDisabled: selectedJobs.length > 0, // 등록된 작업이 있으면 원본언어 비활성화
          preSelectedTargetLanguages: rawTargetLanguages // 등록 작업의 번역언어들 (원본 이름)
        },
          validation: {
            rules: [
              {
                type: 'custom',
                message: '원본언어와 최소 1개 이상의 번역언어를 선택해주세요',
                validator: (value: any) => {
                  return value?.sourceLanguage && value?.targetLanguages && value.targetLanguages.length > 0;
                }
              }
            ]
          }
        },
        {
          id: 'jobTags',
          type: 'simple-tags',
          label: '태그',
          value: [],
          placeholder: '예시) 긴급작업, 고객사:하이브, 분야:교육'
        }
      ]
    },
    {
      id: 'workflow-design',
      title: '워크플로 구성',
      description: 'Task 배치와 순서를 시각적으로 구성합니다.',
      fields: [
        {
          id: 'workflowComponent',
          type: 'workflow-configuration',
          label: '워크플로 설정',
          value: {
            taskNodes: [],
            connections: []
          } as WorkflowData,
          validation: {
            rules: [
              {
                type: 'custom',
                message: '최소 1개 이상의 Task를 추가해주세요',
                validator: (value: any) => {
                  return value?.taskNodes && value.taskNodes.length > 0;
                }
              }
            ]
          }
        }
      ]
    },
    {
      id: 'worker-assignment',
      title: '작업자 배정',
      description: '각 Task에 작업자 또는 작업그룹을 배정합니다.',
      fields: [
        {
          id: 'workerAssignmentComponent',
          type: 'worker-assignment',
          label: '작업자 배정',
          value: {
            assignmentMatrix: [],
            availableWorkers: [],
            availableGroups: []
          } as WorkerAssignmentData,
          validation: {
            rules: [
              {
                type: 'custom',
                message: '작업자 배정이 완료되었습니다',
                validator: () => true // 임시로 항상 유효하다고 설정
              }
            ]
          }
        }
      ]
    },
    {
      id: 'final-confirmation',
      title: '최종 확인',
      description: '설정된 내용을 최종 확인하고 Job을 생성합니다.',
      fields: [
        {
          id: 'finalConfirmationComponent',
          type: 'final-confirmation',
          label: '최종 확인',
          value: {
            jobs: [],
            expandedJobs: [],
            selectedLanguages: []
          } as FinalConfirmationData,
          validation: {
            rules: [
              {
                type: 'custom',
                message: '최종 확인이 완료되었습니다',
                validator: () => true // 임시로 항상 유효하다고 설정
              }
            ]
          }
        }
      ]
    }
  ]
};
}
